import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { pick } from 'lodash';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { useCurrentNoteContextData } from 'lib/context/ClinicalNoteContext/CurrentNoteContext/CurrentNoteContext';
import { Capitalize, io } from 'lib/helpers/utility';

import NotFound from 'components/practice/Dashboard/components/NotFound';
import Skeleton from 'components/shared/Skeleton/Skeleton';

import { useAllForms } from '../../../shared/hooks/useAllForms';
import useIsMultipleTemplate from '../../../shared/hooks/useIsMultipleTemplate';
import MultipleTemplateView from '../../components/MultipleTemplateView';
import AdvancedForm from '../AdvancedForm/AdvancedForm';

const CustomFormNote = ({ context, type: typeProp, allFormsRender }) => {
  const { hpNoteOverviewRefs, enabledNotes } = context;

  const {
    components: ctxComponents,
    setComponents,
    setOverviewData,
    currentHpOverviewData,
    setCurrentHpOverviewData,
    setSaveButtonText,
    updateSaveButton,
    customFormTypes,
    loading
  } = useClinicalNoteContext();
  const { id, appointmentId, formName, type: typeParam } = useParams();
  const location = useLocation();
  const allForms = useAllForms();

  const type = typeProp || typeParam;

  const components = allForms ? ctxComponents?.[type] || {} : ctxComponents;

  const customFormsParams = {
    id,
    appointmentId,
    setOverviewData,
    setCurrentHpOverviewData,
    setSaveButtonText,
    updateSaveButton
  };

  const [formsList, setFormList] = useState({});

  const { typeOfNotes } = useCurrentNoteContextData();

  const note = useMemo(() => typeOfNotes?.[type], [typeOfNotes?.[type]?.components]);

  useEffect(() => {
    const advancedTitles = note?.advancedTitles || {};

    const assignAdvTitlesToComponent = Object.keys(advancedTitles).reduce((acc, key) => {
      if (!advancedTitles[key]?.enabled) return acc;

      const customFormType = customFormTypes?.[type];
      const currAdvForm = customFormType?.find((item) => key === item?.custom_title);

      acc[key] = () => <AdvancedForm currAdvForm={currAdvForm} noteType={type} />;

      return acc;
    }, {});

    setFormList((prevState) => ({ ...prevState, ...assignAdvTitlesToComponent }));
  }, [JSON.stringify(note?.advancedTitles)]);

  useEffect(() => {
    const noteComponents = note?.components || {};

    const enabledComponents = Object.keys(noteComponents)
      .filter((compKey) => noteComponents[compKey].enabled)
      .reduce((prev, curr) => ({ ...prev, [curr]: noteComponents[curr] }), {});

    setComponents((prev) => {
      if (allForms) {
        const newState = pick(
          prev,
          enabledNotes.map((item) => item?.path)
        );

        return {
          ...newState,
          [type]: enabledComponents
        };
      } else {
        return enabledComponents;
      }
    });
  }, [location.search, note?.components]);

  const CurrentForm = formsList && formsList?.[formName ?? Object.values(components)?.[0]?.path];
  const propsForNotFoundNote = {
    icon: 'note-mono',
    iconSize: 128,
    title: 'Note not found',
    titleClassName: '!mt-3',
    description: (
      <div>
        The form{' '}
        {formName && (
          <em className="text-md font-600 text-primary-900">"{Capitalize(formName)}"</em>
        )}{' '}
        you are looking for is not found.
      </div>
    )
  };

  const isMultiTemplate = useIsMultipleTemplate();

  if (loading) return <Skeleton height="78vh" />;

  if (!io(components) || !CurrentForm) return <NotFound {...propsForNotFoundNote} />;

  if (allFormsRender) return null;

  return (
    <div className="flex h-full flex-col gap-y-3">
      {isMultiTemplate ? (
        <MultipleTemplateView note={type} allForms={allForms} />
      ) : (
        <CurrentForm
          key={formName}
          components={components}
          formId={currentHpOverviewData?.[formName]?.id}
          defaultValue={currentHpOverviewData?.[formName]?.response}
          hpNoteOverviewRef={hpNoteOverviewRefs?.[formName]}
          customFormsParams={customFormsParams}
          setCurrentFormData={(newData) => {
            setCurrentHpOverviewData((prevState) => ({
              ...prevState,
              [formName]: { ...prevState[formName], response: newData }
            }));
          }}
        />
      )}
    </div>
  );
};

export default CustomFormNote;
